import { useWatch, type Control, type FieldError, type UseFormRegister } from 'react-hook-form'
import { Input, Label, cn } from '@atrvd/saas-ui'
import { FACILITY_DISPLAY_NAME_MAX_LENGTH } from '@dcp/shared'
import type { FacilityFormData } from '@/lib/facility-form'

interface FacilityDisplayNameFieldProps {
  /** Prefix of the element ids (`add` / `edit`), so both dialogs keep distinct ids. */
  idPrefix: string
  control: Control<FacilityFormData>
  register: UseFormRegister<FacilityFormData>
  error: FieldError | undefined
  disabled: boolean
}

/**
 * The Display Name input of the Add / Edit Facility dialogs, with a hint that
 * the full name belongs in Facility Legal Name and a live length counter.
 *
 * No `maxLength` on the input: a pasted or already-stored longer name must stay
 * visible and be rejected by the form schema, not silently cut. The counter
 * counts the trimmed value, like the schema, and is linked through
 * `aria-describedby` but not live, so it is not announced on every keystroke.
 */
export function FacilityDisplayNameField({
  idPrefix,
  control,
  register,
  error,
  disabled,
}: FacilityDisplayNameFieldProps) {
  const value = useWatch({ control, name: 'name' }) ?? ''
  const length = value.trim().length
  const tooLong = length > FACILITY_DISPLAY_NAME_MAX_LENGTH
  const inputId = `${idPrefix}-facility-name`
  const hintId = `${inputId}-hint`
  const counterId = `${inputId}-counter`
  const errorId = `${inputId}-error`

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        Display Name <span className="text-destructive">*</span>
      </Label>
      <Input
        id={inputId}
        placeholder="Enter display name"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, counterId, error ? errorId : null].filter(Boolean).join(' ')}
        {...register('name')}
      />
      <div className="flex items-start justify-between gap-3 text-sm">
        <p id={hintId} className="text-muted-foreground">
          Short name used to display the facility across the DCP app — in filters, tables,
          drop-downs, and the Cash Position Summary report.
        </p>
        <p
          id={counterId}
          aria-live="off"
          className={cn('shrink-0 tabular-nums', tooLong ? 'text-destructive' : 'text-muted-foreground')}
        >
          {length}/{FACILITY_DISPLAY_NAME_MAX_LENGTH}
        </p>
      </div>
      {error && (
        <p id={errorId} className="text-sm text-destructive">
          {error.message}
        </p>
      )}
    </div>
  )
}
